import { Link } from "react-router-dom";
import { ShieldCheck, BookOpen, BarChart3, Siren, FlaskConical, ArrowRight } from "lucide-react";
import ReviewerLoadStrip from "@/components/dashboard/ReviewerLoadStrip";
import {
  canSeeAdminCenter, canSeeSecurityDashboard, canSeeSystemIntelligence,
  canSeeIncidentResponse, canSeeLeadGeneration, canSeeBetaReport, canSeePreviewTools, roleLabel,
} from "@/lib/roles";

function cardsFor(role) {
  const c = [];
  if (canSeeAdminCenter(role))
    c.push({ to: "/admin", title: "Admin Control Center", desc: "Your assigned operations, compliance and verification queues.", icon: ShieldCheck, tone: "text-[#C2410C] bg-orange-50", testid: "staff-card-admin" });
  if (canSeeLeadGeneration(role))
    c.push({ to: "/lead-generation", title: "Lead Generation", desc: "Review leads and the invitation queue (human-approved).", icon: BookOpen, tone: "text-[#047857] bg-emerald-50", testid: "staff-card-lead-generation" });
  if (canSeeBetaReport(role))
    c.push({ to: "/beta-report", title: "Beta Report", desc: "Beta analytics, funnel and match-quality reporting.", icon: BarChart3, tone: "text-[#047857] bg-emerald-50", testid: "staff-card-beta-report" });
  if (canSeeSecurityDashboard(role))
    c.push({ to: "/security-admin", title: "Security Dashboard", desc: "Security posture, MFA config and event triage.", icon: ShieldCheck, tone: "text-[#C2410C] bg-orange-50", testid: "staff-card-security-admin" });
  if (canSeeSystemIntelligence(role))
    c.push({ to: "/system-intelligence", title: "System Intelligence", desc: "Read-only go/no-go readiness and system oversight.", icon: BookOpen, tone: "text-[#1D4ED8] bg-blue-50", testid: "staff-card-system-intelligence" });
  if (canSeeIncidentResponse(role))
    c.push({ to: "/incident-response", title: "Incident Response", desc: "Breach detection, response and recovery playbooks.", icon: Siren, tone: "text-[#B91C1C] bg-red-50", testid: "staff-card-incident-response" });
  if (canSeePreviewTools(role))
    c.push({ to: "/preview-tools", title: "Preview Tools", desc: "Preview-only seeding and scheduler utilities.", icon: FlaskConical, tone: "text-[#C2410C] bg-orange-50", testid: "staff-card-preview-tools" });
  return c;
}

export default function StaffLanding({ role }) {
  const cards = cardsFor(role);
  return (
    <div data-testid="staff-landing">
      <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600" data-testid="staff-landing-role">
        <ShieldCheck size={13} /> {roleLabel(role)} workspace
      </div>
      <ReviewerLoadStrip role={role} />
      {cards.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500">
          No consoles are assigned to your role yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.to} to={c.to} data-testid={c.testid}
                className="group rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-[#2563EB]/40 transition-all duration-200">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${c.tone}`}><Icon size={18} /></div>
                <div className="mt-3 flex items-center gap-1.5">
                  <h3 className="font-heading font-bold text-[#0A0F1C]">{c.title}</h3>
                  <ArrowRight size={15} className="text-slate-300 group-hover:text-[#2563EB] group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="mt-1 text-sm text-slate-500 leading-snug">{c.desc}</p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
